"use client";

import Link from "next/link";
import { ANIMATED_ICONS, STILL_ANIMATION_ICONS, type AnimatedIconName } from "./animations/animation-icons";
import type { SvgAnimationHandle, SvgAnimationProps } from "./svg-animation/svg-animation";

// Lucide React Icons
import {
  AlertCircle,
  AlertTriangle,
  BadgeCheck,
  BarChart,
  CheckCircle,
  CodeXml,
  Download,
  ExternalLink,
  InfoIcon,
  Loader,
  Mail,
  Plus,
  Minus,
  Save,
  Search,
  Server,
  Settings,
  User,
  UserPlus,
  Check,
  Trash,
  Pencil,
  ImagePlus,
  Square,
  Trash2,
  Sun,
  Moon,
  Send,
  SendHorizontal,
  SlidersHorizontal,
  Maximize2,
  Minimize2,
  ChevronsUpDown,
  ChevronsDownUp,
  ChevronUp,
  Monitor,
  ArrowRight,
  ArrowLeft,
  ArrowUp,
  ChevronLeft,
  ChevronRight,
  X,
  PencilLine,
  Crosshair,
  RotateCcw,
  BrushCleaning,
  ReceiptText,
  ArrowLeftToLine,
  ArrowRightFromLine,
  EllipsisVertical,
  Settings2,
  LogOut,
  ChartColumnBig,
  KeyRound,
  Users,
  HelpCircleIcon,
  Building,
  LockKeyhole,
  Lock,
  LockOpen,
  SquarePen,
  Copy,
  CreditCard,
  SquareArrowOutUpRight,
  Cake,
  Video as Camera,
  Play,
  Pause,
  Rewind,
  FastForward,
  SkipForward,
  SkipBack,
  Timer,
  Eye,
  Undo2,
  ScanEye,
  VolumeX,
  Volume1,
  Volume2,
  Brain,
  Flag,
  FlaskConical,
  Globe,
  GripVertical,
  ThumbsUp,
  ThumbsDown,
  Clapperboard,
  LayoutGrid,
  Locate,
  Lightbulb,
  Link as LinkIcon,
} from "lucide-react";

// Custom Icons
import {
  albumImage,
  addImage,
  annotationSidebarEmpty,
  boxIcon,
  polygon,
  chevronDown,
  closeSidebar,
  code,
  collapse,
  copy as clipboard,
  curlyBraces,
  editSquare,
  handleRemove,
  image,
  imageBackground,
  imageForeground,
  logo,
  outOfSync,
  slash,
  text,
  undo,
  video,
  videoPlaceholder,
  imagePlaceholder,
  logoFullDark,
  logoFullLight,
  logoLight,
  logoDark,
  discord,
  paymentCardLogo,
  visa,
  mastercard,
  americanExpress,
  discover,
} from "../assets/icons";

export const ICON_MAP = {
  ...STILL_ANIMATION_ICONS,
  // Alert & Status Icons
  "alert-circle": AlertCircle,
  "alert-triangle": AlertTriangle,
  "badge-check": BadgeCheck,
  check: Check,
  "check-circle": CheckCircle,
  delete: Trash,
  flag: Flag,
  "thumbs-up": ThumbsUp,
  "thumbs-down": ThumbsDown,
  "trash-2": Trash2,
  square: Square,
  info: InfoIcon,
  loader: Loader,
  "out-of-sync": outOfSync,

  // UI & Navigation Icons
  "chevron-down": chevronDown,
  "chevron-left": ChevronLeft,
  "chevron-right": ChevronRight,
  "chevron-up": ChevronUp,
  globe: Globe,
  "chevron-up-down": ChevronsUpDown,
  "chevrons-down-up": ChevronsDownUp,
  "close-sidebar": closeSidebar,
  x: X,
  collapse: collapse,
  "external-link": ExternalLink,
  link: LinkIcon,
  locate: Locate,
  logo: logo,
  "logo-light": logoLight,
  "logo-dark": logoDark,
  "logo-full-dark": logoFullDark,
  "logo-full-light": logoFullLight,
  plus: Plus,
  minus: Minus,
  search: Search,
  settings: Settings,
  gear: Settings2,
  "sliders-horizontal": SlidersHorizontal,
  "send-horizontal": SendHorizontal,
  send: Send,
  "arrow-right": ArrowRight,
  "arrow-left": ArrowLeft,
  "arrow-up": ArrowUp,
  "layout-grid": LayoutGrid,
  "grip-vertical": GripVertical,
  refresh: RotateCcw,
  "brush-cleaning": BrushCleaning,
  "arrow-left-to-line": ArrowLeftToLine,
  "arrow-right-from-line": ArrowRightFromLine,
  "arrow-right-from-bracket": LogOut,
  "chart-column-big": ChartColumnBig,
  "receipt-text": ReceiptText,
  "key-round": KeyRound,
  "help-circle": HelpCircleIcon,
  "lock-keyhole": LockKeyhole,
  lock: Lock,
  "lock-open": LockOpen,
  "square-pen": SquarePen,
  "square-arrow-out-up-right": SquareArrowOutUpRight,
  cake: Cake,
  play: Play,
  pause: Pause,
  rewind: Rewind,
  "fast-forward": FastForward,
  "skip-forward": SkipForward,
  "skip-back": SkipBack,
  timer: Timer,
  volume: Volume2,
  eye: Eye,
  "scan-eye": ScanEye,
  "undo-2": Undo2,
  brain: Brain,
  "flask-conical": FlaskConical,

  // Content & Media Icons
  "album-image": albumImage,
  "add-image": addImage,
  "image-plus": ImagePlus,
  pencil: Pencil,
  image: image,
  "image-background": imageBackground,
  "image-foreground": imageForeground,
  text: text,
  video: video,
  "image-placeholder": imagePlaceholder,
  "video-placeholder": videoPlaceholder,
  maximize: Maximize2,
  minimize: Minimize2,
  "pencil-line": PencilLine,
  "payment-card-logo": paymentCardLogo,
  visa: visa,
  mastercard: mastercard,
  "american-express": americanExpress,
  discover: discover,
  "credit-card": CreditCard,
  camera: Camera,
  "volume-x": VolumeX,
  "volume-1": Volume1,
  "volume-2": Volume2,
  clapperboard: Clapperboard,
  lightbulb: Lightbulb,

  // Code & Development Icons
  code: code,
  "code-xml": CodeXml,
  clipboard: clipboard,
  copy: Copy,
  "curly-braces": curlyBraces,
  slash: slash,

  // User & Team Icons
  user: User,
  "user-plus": UserPlus,
  users: Users,
  building: Building,

  // Actions & Tools Icons
  download: Download,
  "edit-square": editSquare,
  email: Mail,
  "handle-remove": handleRemove,
  save: Save,
  undo: undo,
  "ellipsis-vertical": EllipsisVertical,

  // Data & Analytics Icons
  "bar-chart": BarChart,
  server: Server,

  // Misc Icons
  "annotation-sidebar-empty": annotationSidebarEmpty,
  "box-icon": boxIcon,
  polygon: polygon,
  crosshair: Crosshair,
  sun: Sun,
  moon: Moon,
  monitor: Monitor,

  // Social Icons
  discord: discord,
};

export type IconName = keyof typeof ICON_MAP;

interface BaseIconProps extends React.SVGProps<SVGSVGElement> {
  name: IconName;
  href?: string;
  className?: string;
  "data-testid"?: string;
  alt?: string;
}

type PlaybackProps = Pick<SvgAnimationProps, "autoplay" | "direction" | "loop" | "speed">;
export type IconProps =
  | (BaseIconProps & {
      animated?: false;
      animationRef?: never;
      autoplay?: never;
      loop?: never;
    })
  | (Omit<BaseIconProps, "name" | "direction" | "speed"> &
      PlaybackProps & {
        name: AnimatedIconName;
        animated?: boolean;
        animationRef?: React.Ref<SvgAnimationHandle>;
      });

const Icon: React.FC<IconProps> = (props) => {
  const {
    name,
    href,
    className = "",
    ref,
    animated,
    animationRef,
    autoplay,
    direction,
    loop,
    speed,
    ...svgProps
  } = props;
  const iconProps = {
    ...(!href && { "aria-label": name, "data-testid": svgProps["data-testid"] || `icon-${name}` }),
    className,
    ...svgProps,
  };

  let icon;
  if (animated) {
    const AnimatedIcon = ANIMATED_ICONS[props.name];
    icon = (
      <AnimatedIcon
        aria-hidden={false}
        height={24}
        width={24}
        {...iconProps}
        autoplay={autoplay ?? true}
        direction={props.direction}
        {...(loop !== undefined && { loop })}
        ref={animationRef}
        speed={props.speed}
        svgRef={ref}
      />
    );
  } else {
    const IconComponent = ICON_MAP[name];
    icon = <IconComponent {...iconProps} direction={direction} ref={ref} speed={speed} />;
  }

  if (!!href) {
    return (
      <Link aria-label={name} data-testid={props["data-testid"] || `icon-${name}`} href={href}>
        {icon}
      </Link>
    );
  }

  return icon;
};

export { Icon };
